// src/components/AdminGallery.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const STORAGE_BUCKET = 'gallery'; // Supabase storage bucket

const AdminGallery = ({ onLogout }) => {
  const navigate = useNavigate();
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState('Photos');
  const [selectedFile, setSelectedFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Get video thumbnail
  const getVideoThumbnail = (videoUrl) => {
    if (!videoUrl) return '/images/placeholder-video.jpg';
    if (videoUrl.includes('youtube.com/watch') || videoUrl.includes('youtu.be/')) {
      let videoId = '';
      if (videoUrl.includes('youtube.com/watch')) videoId = videoUrl.split('v=')[1]?.split('&')[0];
      else if (videoUrl.includes('youtu.be/')) videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '/images/placeholder-video.jpg';
    }
    if (videoUrl.includes('instagram.com/')) return '/images/placeholder-video.jpg';
    if (videoUrl.includes('vimeo.com/')) {
      const match = videoUrl.match(/vimeo\.com\/(\d+)/);
      return match ? `https://vumbnail.com/${match[1]}.jpg` : '/images/placeholder-video.jpg';
    }
    return '/images/placeholder-video.jpg';
  };


  const showSuccess = (msg) => {
    const n = document.createElement('div');
    n.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg z-50 animate-slideInRight';
    n.innerHTML = `<div class="flex items-center gap-2"><span>✅</span><span>${msg}</span></div>`;
    document.body.appendChild(n);
    setTimeout(() => n.remove(), 3000);
  };

  const showError = (msg) => {
    const n = document.createElement('div');
    n.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-xl shadow-lg z-50 animate-slideInRight';
    n.innerHTML = `<div class="flex items-center gap-2"><span>❌</span><span>${msg}</span></div>`;
    document.body.appendChild(n);
    setTimeout(() => n.remove(), 3000);
  };

  const loadGalleryImages = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('gallery')
        .select('*')
        .eq('section', selectedSection)
        .order('created_at', { ascending: false });

      if (error) {
        console.error(error);
        showError('Failed to load gallery items');
        setGalleryImages([]);
      } else setGalleryImages(data || []);
    } catch (err) {
      console.error(err);
      showError('Error loading gallery items');
      setGalleryImages([]);
    } finally {
      setLoading(false);
    }
  }, [selectedSection]);

  const uploadGalleryItem = async () => {
    if (selectedSection === 'Videos') {
      if (!videoUrl.trim()) { showError('Enter a video URL'); return; }
      const pattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be|instagram\.com|vimeo\.com)/;
      if (!pattern.test(videoUrl.trim())) { showError('Enter a valid video URL'); return; }
    } else {
      if (!selectedFile) { showError('Select an image file'); return; }
      if (selectedFile.size > 4 * 1024 * 1024) { showError('File too large (max 4MB)'); return; }
    }

    setUploading(true);
    setUploadProgress(0);
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => (prev >= 90 ? prev : prev + Math.random() * 15));
    }, 200);

    try {
      if (selectedSection === 'Videos') {
        const { error } = await supabase.from('gallery').insert([{
          section: selectedSection,
          image_url: videoUrl.trim(),
          storage_path: null,
          is_active: true
        }]);
        clearInterval(progressInterval);
        setUploadProgress(100);
        if (error) showError('Failed to add video');
        else { showSuccess('Video added!'); setVideoUrl(''); loadGalleryImages(); }
      } else {
        const timestamp = Date.now();
        const safeName = selectedFile.name.replace(/\s+/g, '_');
        const path = `${selectedSection}/${new Date().toISOString().slice(0,10)}_${timestamp}_${safeName}`;

        const { error: uploadErr } = await supabase.storage.from(STORAGE_BUCKET).upload(path, selectedFile, { upsert: false });
        if (uploadErr) { clearInterval(progressInterval); showError('Failed to upload image'); setUploading(false); return; }

        const { data: publicUrlData } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
        const publicUrl = publicUrlData?.publicUrl || null;

        const { error } = await supabase.from('gallery').insert([{
          section: selectedSection,
          image_url: publicUrl,
          storage_path: path,
          is_active: true
        }]);
        clearInterval(progressInterval);
        setUploadProgress(100);

        if (error) { showError('Failed to save image'); try { await supabase.storage.from(STORAGE_BUCKET).remove([path]); } catch {} }
        else { showSuccess('Image uploaded!'); setSelectedFile(null); document.getElementById('galleryImage').value=''; loadGalleryImages(); }
      }
    } catch (err) {
      console.error(err);
      clearInterval(progressInterval);
      showError('Upload error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteImage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const { data: row, error: fetchErr } = await supabase.from('gallery').select('id, storage_path').eq('id', id).single();
      if (fetchErr) { showError('Failed to fetch item'); return; }

      if (row.storage_path) await supabase.storage.from(STORAGE_BUCKET).remove([row.storage_path]);
      const { error: delErr } = await supabase.from('gallery').delete().eq('id', id);
      if (delErr) showError('Failed to delete item');
      else { showSuccess('Item deleted'); loadGalleryImages(); }
    } catch (err) {
      console.error(err);
      showError('Delete error');
    }
  };

  useEffect(() => { loadGalleryImages(); }, [loadGalleryImages]);

  return (
    <div className="min-h-screen">
      <header className="bg-gradient-to-r from-royal-blue to-blue-800 text-white py-6 mb-8">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl font-semibold flex items-center gap-2">
              ← Back
            </button>
            <h1 className="text-2xl font-bold flex items-center gap-2">🖼️ Gallery Management</h1>
          </div>
          <button onClick={onLogout} className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl font-semibold flex items-center gap-2">🚪 Logout</button>
        </div>
      </header>

      <div className="container mx-auto px-4 pb-12">
        {/* Upload Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-lg">
          <h2 className="text-2xl font-bold mb-6">{selectedSection === 'Videos' ? 'Add Video' : 'Upload Image'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold mb-3">Section:</label>
              <select value={selectedSection} onChange={(e) => { setSelectedSection(e.target.value); setSelectedFile(null); setVideoUrl(''); document.getElementById('galleryImage').value=''; }} className="w-full px-4 py-3 border rounded-xl">
                <option value="Photos">📷 Photos</option>
                <option value="Videos">🎥 Videos</option>
                <option value="Annual Day">🎉 Annual Day</option>
                <option value="Sports">⚽ Sports</option>
                <option value="Cultural Events">🎭 Cultural Events</option>
                <option value="Classroom">🏫 Classroom</option>
              </select>
            </div>

            <div>
              {selectedSection === 'Videos' ? (
                <>
                  <label className="block text-sm font-semibold mb-3">Video URL:</label>
                  <input type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://..." className="w-full px-4 py-3 border rounded-xl"/>
                </>
              ) : (
                <>
                  <label className="block text-sm font-semibold mb-3">Image File:</label>
                  <input type="file" id="galleryImage" accept="image/*" onChange={(e) => setSelectedFile(e.target.files[0])} className="w-full px-4 py-3 border rounded-xl"/>
                  {selectedFile && <div className="mt-2 text-sm">{selectedFile.name} ({(selectedFile.size/(1024*1024)).toFixed(1)} MB)</div>}
                </>
              )}
            </div>
          </div>

          <button onClick={uploadGalleryItem} disabled={uploading || (selectedSection==='Videos'? !videoUrl.trim() : !selectedFile)} className="px-8 py-3 rounded-xl font-semibold bg-gradient-to-r from-royal-blue to-blue-800 text-white hover:scale-105">
            {uploading ? 'Uploading...' : selectedSection==='Videos'? 'Add Video' : 'Upload Image'}
          </button>

          {uploading && (
            <div className="mt-4 w-full bg-slate-200 h-2 rounded-full overflow-hidden">
              <div className="bg-gradient-to-r from-royal-blue to-blue-800 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
            </div>
          )}
        </div>

        {/* Gallery Display */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">
              Current {selectedSection==='Videos'? 'Videos':'Images'}
            </h2>
            <div className="text-sm text-slate-600 bg-white/50 px-3 py-1 rounded-full">{galleryImages.length}</div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-royal-blue"></div>
              <span className="ml-3">Loading...</span>
            </div>
          ) : galleryImages.length > 0 ? (
            selectedSection === 'Videos' ? (
              <div className="space-y-4">
                {galleryImages.map(item => (
                  <div key={item.id} className="flex items-center justify-between bg-slate-100 p-2 rounded-xl shadow-sm">
                    <div className="flex items-center gap-4 cursor-pointer" onClick={() => window.open(item.image_url, '_blank')}>
                      <img src={getVideoThumbnail(item.image_url)} alt="video thumbnail" className="w-20 h-12 object-cover rounded" />
                      <div className="font-medium text-slate-700 truncate">{item.image_url}</div>
                    </div>
                    <button
                      onClick={() => deleteImage(item.id)}
                      className="bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center"
                      title="Delete Video"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                {galleryImages.map(item => (
                  <div key={item.id} className="relative rounded-xl overflow-hidden shadow-lg">
                    <img src={item.image_url} alt={item.alt_text || 'image'} className="w-full h-48 object-cover"/>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteImage(item.id); }}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center"
                      title="Delete Image"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )
          ) : <p className="text-center text-slate-500">No items in this section yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default AdminGallery;
